import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-1448678b"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "periodontal-page" }

import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import PeriodontalMap from '@/pages/PeriodontalPage/components/PeriodotalMap.vue'
import PeriodontalPreview from '@/pages/PeriodontalPage/components/PeriodontalPreview.vue'
import PeriodontalPdf from '@/pages/PeriodontalPage/components/pdf/PeriodontalPdf.vue'
import { WConsolidatedReport } from '@/widgets/WConsolidatedReport'
import { WReportHeader } from '@/widgets/WReportHeader'
import { WReportSubmit } from '@/widgets/WReportSubmit'
import { WReportPrint } from '@/widgets/WReportPrint'
import { WSendToBot } from '@/widgets/WSendToBot'
import { WReportComments } from '@/widgets/WReportComments'
import {
  getDateFormat,
  PeriodontalData,
  showErrorMessage,
} from '@/shared/utils'
import {
  CommentTemplateType,
  CommentType,
  ConsolidatedDataType,
  ConsolidatedTableType,
  ConsultationType,
  FullPeriodontalType,
  PeriodontalDetailType,
  PeriodontalReportType,
  RemoteCommentsType,
} from '@/shared/types'
import { DATE_FORMAT, JAW_TYPES, REPORT_VIEW } from '@/shared/constants'
import { usePeriodontalReport } from '@/shared/composables'
import {
  organizationsApi,
  patientApi,
  periodontalReportApi,
  userApi,
} from '@/shared/api'
import { WReportRecommendations } from '@/widgets/WReportRecommendations'


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const { isReportUpdated, setUpdateReport } = usePeriodontalReport()

const route = useRoute()

const periodontalData = ref<PeriodontalReportType>({
  firstFacialData: new PeriodontalData(JAW_TYPES.jawUp),
  firstTongueData: new PeriodontalData(JAW_TYPES.jawUp, true),
  secondFacialData: new PeriodontalData(JAW_TYPES.jawDown),
  secondTongueData: new PeriodontalData(JAW_TYPES.jawDown),
  firstFacialDiagram: undefined,
  firstTongueDiagram: undefined,
  secondFacialDiagram: undefined,
  secondTongueDiagram: undefined,
})

const previousPeriodontalData = ref<PeriodontalReportType>({
  firstFacialData: new PeriodontalData(JAW_TYPES.jawUp),
  firstTongueData: new PeriodontalData(JAW_TYPES.jawUp, true),
  secondFacialData: new PeriodontalData(JAW_TYPES.jawDown),
  secondTongueData: new PeriodontalData(JAW_TYPES.jawDown),
  firstFacialDiagram: undefined,
  firstTongueDiagram: undefined,
  secondFacialDiagram: undefined,
  secondTongueDiagram: undefined,
})

const consolidatedData = ref<ConsolidatedDataType>({
  diabetes: false,
  smoking: false,
})

const reportInfo = ref<PeriodontalDetailType>({
  id: 0,
  date_at: '',
  organization: undefined,
  doctor: undefined,
})

const loading = ref(false)
const comments = ref<CommentType[]>([])
const commentTemplates = ref<CommentTemplateType[]>([])
const email = ref('')

const consultation = ref<ConsultationType>({
  consultations: [],
  fullConsultationList: undefined,
  reCleaning: 0,
})

const consolidatedPlaque = computed(() => {
  const value =
    periodontalData.value?.firstFacialData.getActivePlaque() +
    periodontalData.value?.firstTongueData.getActivePlaque() +
    periodontalData.value?.secondFacialData.getActivePlaque() +
    periodontalData.value?.secondTongueData.getActivePlaque()

  return Math.round((value * 100) / 192)
})

const consolidatedBleeding = computed(() => {
  const value =
    periodontalData.value?.firstFacialData.getActiveBleeding() +
    periodontalData.value?.firstTongueData.getActiveBleeding() +
    periodontalData.value?.secondFacialData.getActiveBleeding() +
    periodontalData.value?.secondTongueData.getActiveBleeding()

  return Math.round((value * 100) / 192)
})

const consolidatedPockets = computed(() => {
  return (
    Number(consolidatedPocketsTable.value[1].four) +
    Number(consolidatedPocketsTable.value[1].five) +
    Number(consolidatedPocketsTable.value[1].moreSix)
  )
})

const consolidatedPocketsTable = computed((): ConsolidatedTableType[] => [
  {
    four: pocketFour.value,
    five: pocketFive.value,
    moreSix: pocketMoreSix.value,
  },
  {
    four: Number(Math.round((pocketFour.value * 100) / 192).toFixed(2)),
    five: Number(Math.round((pocketFive.value * 100) / 192).toFixed(2)),
    moreSix: Number(Math.round((pocketMoreSix.value * 100) / 192).toFixed(2)),
  },
  {
    four: Number(
      (
        Math.round((pocketFour.value * 100) / 192) +
        Math.round((pocketFive.value * 100) / 192) +
        Math.round((pocketMoreSix.value * 100) / 192)
      ).toFixed(2)
    ),
    five: Number(
      (
        Math.round((pocketFive.value * 100) / 192) +
        Math.round((pocketMoreSix.value * 100) / 192)
      ).toFixed(2)
    ),
    moreSix: Number(Math.round((pocketMoreSix.value * 100) / 192).toFixed(2)),
  },
])

const pocketFour = computed(() => {
  const value =
    periodontalData.value?.firstFacialData.getSoundingValue(4) +
    periodontalData.value?.firstTongueData.getSoundingValue(4) +
    periodontalData.value?.secondFacialData.getSoundingValue(4) +
    periodontalData.value?.secondTongueData.getSoundingValue(4)

  return value
})

const pocketFive = computed(() => {
  const value =
    periodontalData.value?.firstFacialData.getSoundingValue(5) +
    periodontalData.value?.firstTongueData.getSoundingValue(5) +
    periodontalData.value?.secondFacialData.getSoundingValue(5) +
    periodontalData.value?.secondTongueData.getSoundingValue(5)

  return value
})

const pocketMoreSix = computed(() => {
  const value =
    periodontalData.value?.firstFacialData.getSoundingValue(6, true) +
    periodontalData.value?.firstTongueData.getSoundingValue(6, true) +
    periodontalData.value?.secondFacialData.getSoundingValue(6, true) +
    periodontalData.value?.secondTongueData.getSoundingValue(6, true)

  return value
})

const reportId = computed(() => Number(route.params.reportId))
const patientId = computed(() => Number(route.params.patientId))
const isPreviewMode = computed((): boolean => route.query['view-mode'] === '1')

watch(reportId, (newReportId) => {
  if (route.name === 'PeriodontalPage') {
    if (newReportId) {
      getFullReport()
    } else {
      initEmptyPeriodontalData()
    }
  }
})

watch(
  () => isReportUpdated.value,
  () => {
    getFullReport()
    setUpdateReport(false)
  }
)

const initEmptyPeriodontalData = () => {
  periodontalData.value.firstFacialData = new PeriodontalData(JAW_TYPES.jawUp)
  periodontalData.value.firstTongueData = new PeriodontalData(
    JAW_TYPES.jawUp,
    true
  )
  periodontalData.value.secondFacialData = new PeriodontalData(
    JAW_TYPES.jawDown
  )
  periodontalData.value.secondTongueData = new PeriodontalData(
    JAW_TYPES.jawDown
  )

  periodontalData.value.firstFacialDiagram =
    periodontalData.value.firstFacialData.getDiagramData()
  periodontalData.value.firstTongueDiagram =
    periodontalData.value.firstTongueData.getDiagramData()
  periodontalData.value.secondFacialDiagram =
    periodontalData.value.secondFacialData.getDiagramData()
  periodontalData.value.secondTongueDiagram =
    periodontalData.value.secondTongueData.getDiagramData()

  consolidatedData.value.diabetes = false
  consolidatedData.value.smoking = false

  reportInfo.value.id = 0
  reportInfo.value.date_at = ''
  reportInfo.value.organization = undefined
  reportInfo.value.doctor = undefined

  comments.value = []
}

const getFullReport = async (): Promise<void> => {
  if (!reportId.value) return

  loading.value = true

  const [error, response] = await periodontalReportApi.getReport(reportId.value)

  if (error) {
    showErrorMessage(error)
  } else {
    const report = response.data.report
    reportInfo.value = report
    consultation.value.reCleaning = report.re_cleaning

    consolidatedData.value.smoking = Boolean(report.smoking) ?? false
    consolidatedData.value.diabetes = Boolean(report.diabetes) ?? false

    getReportData(JSON.parse(report.index))
    getComments(response.data)
    await getUserInfo(report.user_id)
    await getOrganizationInfo(report.organization_id)
    await getPatientEmail()
  }

  loading.value = false

  if (!error) {
    await getPreviousReportData()
  }
}

const getReportData = (report: FullPeriodontalType) => {
  periodontalData.value.firstFacialData.setData(report.firstFacialData)
  periodontalData.value.firstTongueData.setData(report.firstTongueData)
  periodontalData.value.secondFacialData.setData(report.secondFacialData)
  periodontalData.value.secondTongueData.setData(report.secondTongueData)

  periodontalData.value.firstFacialDiagram =
    periodontalData.value.firstFacialData.getDiagramData()
  periodontalData.value.firstTongueDiagram =
    periodontalData.value.firstTongueData.getDiagramData()
  periodontalData.value.secondFacialDiagram =
    periodontalData.value.secondFacialData.getDiagramData()
  periodontalData.value.secondTongueDiagram =
    periodontalData.value.secondTongueData.getDiagramData()
}

const getPreviousReportData = async () => {
  const [error, response] = await periodontalReportApi.getPreviousReport(
    reportId.value
  )
  if (error) {
    showErrorMessage(error)
  } else {
    const report = response.data.index

    if (!response.data.id) {
      return
    }

    previousPeriodontalData.value.firstFacialData.setData(
      report.firstFacialData
    )
    previousPeriodontalData.value.firstTongueData.setData(
      report.firstTongueData
    )
    previousPeriodontalData.value.secondFacialData.setData(
      report.secondFacialData
    )
    previousPeriodontalData.value.secondTongueData.setData(
      report.secondTongueData
    )

    previousPeriodontalData.value.firstFacialDiagram =
      previousPeriodontalData.value.firstFacialData.getDiagramData()
    previousPeriodontalData.value.firstTongueDiagram =
      previousPeriodontalData.value.firstTongueData.getDiagramData()
    previousPeriodontalData.value.secondFacialDiagram =
      previousPeriodontalData.value.secondFacialData.getDiagramData()
    previousPeriodontalData.value.secondTongueDiagram =
      previousPeriodontalData.value.secondTongueData.getDiagramData()
  }
}

const getUserInfo = async (userId: number): Promise<void> => {
  const [error, response] = await userApi.getUser(userId)

  if (error) {
    showErrorMessage(error)
  } else {
    reportInfo.value.doctor = response.data.user
  }
}

const getOrganizationInfo = async (organizationId: number): Promise<void> => {
  const [error, response] = await organizationsApi.getOrganizationInfo(
    organizationId
  )

  if (error) {
    showErrorMessage(error)
  } else {
    reportInfo.value.organization = response.data.organization
  }
}

const getComments = (remoteComments: RemoteCommentsType): void => {
  commentTemplates.value = []
  comments.value = remoteComments.comments

  remoteComments.comment_templates.forEach((template) => {
    const newTemplate: CommentTemplateType = {
      template_id: template.id,
      template: template.template,
      isVisible: false,
    }

    commentTemplates.value.push(newTemplate)
  })
}

const getPatientEmail = async (): Promise<void> => {
  const [error, response] = await patientApi.getPatientInfo(
    Number(patientId.value)
  )

  if (error) {
    showErrorMessage(error)
  } else {
    email.value = response.data.patient.email
  }
}

onMounted(async (): Promise<void> => {
  if (reportId.value) {
    await getFullReport()
  } else {
    initEmptyPeriodontalData()
  }
})

return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["periodontal-page__wrapper", { 'periodontal-page__wrapper--preview': _unref(isPreviewMode) }])
    }, [
      _createVNode(PeriodontalPdf, {
        class: "periodontal-page__pdf",
        "re-cleaning": consultation.value.reCleaning,
        "report-info": reportInfo.value,
        data: periodontalData.value,
        "previous-data": previousPeriodontalData.value,
        plaque: _unref(consolidatedPlaque),
        bleeding: _unref(consolidatedBleeding),
        pockets: _unref(consolidatedPockets),
        "pockets-table": _unref(consolidatedPocketsTable),
        comments: comments.value,
        "consolidated-data": consolidatedData.value
      }, null, 8, ["re-cleaning", "report-info", "data", "previous-data", "plaque", "bleeding", "pockets", "pockets-table", "comments", "consolidated-data"]),
      (reportInfo.value.id)
        ? (_openBlock(), _createBlock(_unref(WReportHeader), {
            key: 0,
            "report-info": reportInfo.value,
            "report-type": _unref(REPORT_VIEW).periodontal
          }, null, 8, ["report-info", "report-type"]))
        : _createCommentVNode("", true),
      _withDirectives(_createVNode(PeriodontalPreview, {
        "report-info": reportInfo.value,
        data: periodontalData.value,
        "previous-data": previousPeriodontalData.value,
        plaque: _unref(consolidatedPlaque),
        "re-cleaning": consultation.value.reCleaning,
        bleeding: _unref(consolidatedBleeding),
        pockets: _unref(consolidatedPockets),
        "pockets-table": _unref(consolidatedPocketsTable),
        comments: comments.value,
        "consolidated-data": consolidatedData.value
      }, null, 8, ["report-info", "data", "previous-data", "plaque", "re-cleaning", "bleeding", "pockets", "pockets-table", "comments", "consolidated-data"]), [
        [_vShow, _unref(isPreviewMode)]
      ]),
      (!_unref(isPreviewMode))
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createVNode(PeriodontalMap, {
              class: "periodontal-page__card",
              data: periodontalData.value,
              "previous-data": previousPeriodontalData.value
            }, null, 8, ["data", "previous-data"]),
            (reportInfo.value.id)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createVNode(_unref(WConsolidatedReport), {
                    class: "periodontal-page__card",
                    plaque: _unref(consolidatedPlaque),
                    bleeding: _unref(consolidatedBleeding),
                    pockets: _unref(consolidatedPockets),
                    "pockets-table": _unref(consolidatedPocketsTable),
                    "re-cleaning": consultation.value.reCleaning,
                    "consolidated-data": consolidatedData.value
                  }, null, 8, ["plaque", "bleeding", "pockets", "pockets-table", "re-cleaning", "consolidated-data"]),
                  _createVNode(_unref(WReportRecommendations), {
                    "possibility-appoint-consultation": true,
                    consultation: consultation.value,
                    "is-periodontal-report": true,
                    class: "periodontal-page__card",
                    onUpdated: getFullReport
                  }, null, 8, ["consultation"]),
                  (_openBlock(), _createBlock(_unref(WReportComments), {
                    class: "periodontal-page__card",
                    key: comments.value,
                    comments: comments.value,
                    templates: commentTemplates.value,
                    "report-type": _unref(REPORT_VIEW).periodontal,
                    onUpdate: getFullReport
                  }, null, 8, ["comments", "templates", "report-type"])),
                  _createVNode(_unref(WReportSubmit), {
                    "report-type": _unref(REPORT_VIEW).periodontal,
                    email: email.value
                  }, null, 8, ["report-type", "email"]),
                  _createVNode(_unref(WReportPrint), { "url-part": 'periodontology' })
                ], 64))
              : _createCommentVNode("", true)
          ], 64))
        : _createCommentVNode("", true),
      (reportInfo.value.id && _unref(isPreviewMode))
        ? (_openBlock(), _createBlock(_unref(WReportHeader), {
            key: 2,
            "report-info": reportInfo.value,
            "report-type": _unref(REPORT_VIEW).periodontal,
            position: "bottom"
          }, null, 8, ["report-info", "report-type"]))
        : _createCommentVNode("", true)
    ], 2)
  ])), [
    [
      _directive_loading,
      loading.value,
      void 0,
      { body: true }
    ]
  ])
}
}

})